<template lang="pug">
    div.in-app-chat-container.mr-5
        div(@click='toggleInAppChat')
            div.badge(v-if="totalUnreadInAppMessages > 0")
                span.badge-text {{ totalUnreadInAppMessages }}
            v-icon.volie-color-blue mdi-chat
        PopupWindow(
            :is-visible="displayInAppChat"
            :min-width="chatStartWidth"
            :min-height="chatStartHeight"
            :start-width="chatStartWidth"
            :start-height="chatStartHeight"
            :on-close="() => displayInAppChat = false"
            :offset-left="chatOffsetLeft" 
            :offset-top="chatOffsetTop"
        )
            template(v-slot:header)
                span.text-ih7(style="width: 100%;") Chat
            template(v-slot:body)
                div.window-body
                    v-row.d-flex.pa-1.ma-0.chat-actions-row
                        div.d-flex.align-center(v-show="isConversationView")
                            v-tooltip(top)
                                template(v-slot:activator='{ on, attrs }')
                                    span(v-bind="attrs" v-on="on" role="text")
                                        v-icon.ml-1(@click="handleBack" v-show="isConversationView") mdi-chevron-left
                                span
                                    | Back
                                    
                            span.ml-1.cursor-default(v-show="isConversationView") 
                                | {{ displayGroupName }}
                                v-menu(
                                        v-if="isLargeGroupChat"
                                        right
                                        open-on-hover
                                        offset-x
                                        offset-y
                                    )
                                    template(v-slot:activator='{ on, attrs }')
                                        span.ml-1.see-all-participants(v-bind="attrs" v-on="on" role="text")
                                            v-icon(small) mdi-information-outline
                                    ul.participants-list
                                        li.participant-item(
                                            v-for="(participant, index) in chatParticipants" 
                                            :key="index"
                                            )
                                            | {{ participant }}
                        v-spacer

                        div(v-show="isGroupsView")
                            v-tooltip(top)
                                template(v-slot:activator='{ on, attrs }')
                                    span(v-bind="attrs" v-on="on" role="text")
                                        v-icon.cursor-pointer.mr-1(@click="handleNewMessage") mdi-square-edit-outline
                                span
                                    | New Message                        

                    div.main-container
                        transition(name="slide-x")
                            div.groups-view(v-show="isGroupsView" key="groups")
                                div.conversation-group-cards(
                                    v-show="validConversationGroups"
                                    ref="conversationGroupCards"
                                )
                                    ConversationGroupCard(
                                        v-for="conversationGroup in conversationGroups" 
                                        @view-conversation="handleViewConversation(conversationGroup.conversation_group_id)"
                                        :key="conversationGroup.conversation_group_id" 
                                        :conversation-group="conversationGroup"
                                    )
                                    div.text-center.my-2(v-if="isShowMoreLoading")
                                        v-progress-circular(
                                            :size='25'
                                            :width='2'
                                            color='var(--volie-blue)'
                                            indeterminate
                                        )
                                template(v-if="!validConversationGroups")
                                    div.d-flex.justify-center.align-center.pa-4
                                        span.cursor-default(style="color: var(--lt-gray);") No messages
                        transition(name="slide-x")
                            div.conversation-view(v-show="isConversationView" key="conversation")
                                ConversationView(
                                    :conversation-group-id="selectedConversationGroupId"
                                    :is-group-chat="isGroupChat"
                                    ref="conversationView"
                                    @view-conversation="handleViewConversation"
                                )
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components
import ConversationGroupCard from '../in_app_chat/conversation_group_card.vue';
import ConversationView from '../in_app_chat/conversation_view.vue';
import PopupWindow from './popup_window.vue';

// Data 
import { GetConversationGroups, ReadAllConversationGroupUnreadMessages } from '@/data/conversation_group';

// Utils
import { isShowMore } from '@/helpers/show_more';

export default Vue.extend({
    data() {
        return {
            activeSlide: 'groups',
            bus: VolieState.websocketClient().eventBus(),
            chatOffsetLeft: 0,
            chatOffsetTop: 0,
            chatStartWidth: 400,
            chatStartHeight: 500,
            chatParticipants: [],
            conversationGroups: [],
            conversationGroupsContainer: null,
            displayInAppChat: false,
            isShowMoreLoading: false,
            isLoadingConversationGroups: false,
            isShowMoreable: true,
            limit: 25,
            offset: 0,
            selectedConversationGroupId: 0,
            tempGroupName: '',
            totalUnreadInAppMessages: 0,
        }
    },
    methods: {
        getConversationGroups() {
            if (this.isLoadingConversationGroups) {
                return;
            }

            const options = {
                group_type: 'user_to_user',
                limit: this.limit || 25,
                offset: this.offset || 0,
            }

            if (this.conversationGroups?.length > 0) {
                    this.isShowMoreLoading = true;
            } else {
                this.isLoadingConversationGroups = true;
            }

            GetConversationGroups(options)
                .then((response) => {
                    if (response?.Data && response?.Data?.conversation_groups?.length > 0) {
                        const data = response?.Data?.conversation_groups;
                        this.isShowMoreable = isShowMore(data.length, this.limit);

                        if (this.conversationGroups?.length > 0) {
                            this.conversationGroups = this.conversationGroups.concat(data);
                        } else {
                            this.conversationGroups = data;
                            this.initScrollListener();
                        }

                    } else {
                        this.isShowMoreable = false;
                        this.removeScrollListener();
                    }
                    
                    if (response?.Error) {
                        if (response?.Error !== 'Zero conversation groups found') {
                            this.isShowMoreable = false;
                            this.removeScrollListener();
                            console.error('error getting conversation groups:', response.Error);
                        }
                    }
                })
                .catch((error) => {
                    this.isShowMoreable = false;
                    this.removeScrollListener();
                    console.error('error caught when getting conversation groups:', error);
                })
                .finally(() => {
                    this.isShowMoreLoading = false;
                    this.isLoadingConversationGroups = false;
                });
        },
        handleScroll() {
            if (!this.conversationGroupsContainer) {
                return;
            }

            // check if scrolled to bottom
            if (this.conversationGroupsContainer.scrollTop + this.conversationGroupsContainer.clientHeight < this.conversationGroupsContainer.scrollHeight) {
                return;
            }

            // ensure we are not already loading more
            if (this.isShowMoreLoading) {
                return;
            }

            // if we know that there aren't any more, do nothing
            if (!this.isShowMoreable) {
                return;
            }

            this.loadMoreConversationGroups();
        },
        initScrollListener() {
            // if we already have a div, do nothing (means we are loading more or sorting)
            if (this.conversationGroupsContainer || !this.validConversationGroups) {
                return;
            }

            this.conversationGroupsContainer = this.$refs.conversationGroupCards;

            if (!this.conversationGroupsContainer) {
                return;
            }

            // add scroll listener
            this.conversationGroupsContainer.addEventListener('scroll', this.handleScroll);
        },
        removeScrollListener() {
            if (!this.conversationGroupsContainer) {
                return;
            }

            // unset
            this.conversationGroupsContainer.removeEventListener('scroll', this.handleScroll);
            this.conversationGroupsContainer = null;
        },
        handleNewMessage() {
            this.selectedConversationGroupId = 0;
            this.tempGroupName = '';
            this.chatParticipants = [];
            this.activeSlide = 'conversation';
            this.$refs.conversationView.resetMessageState();
        },
        handleViewConversation(conversationGroupId: number) {
            this.selectedConversationGroupId = conversationGroupId;
            this.activeSlide = 'conversation';

            const group = this.conversationGroups.find(g => g.conversation_group_id === conversationGroupId);
            if (group) {
                this.chatParticipants = group.other_user_names.split(', ');
                this.tempGroupName = group.other_user_names;
            }

            this.readAllConversationGroupUnreadMessages(conversationGroupId, this.currentUser.activated_account_id);
        },
        handleBack() {
            this.activeSlide = 'groups';
            this.selectedConversationGroupId = 0;
        },
        init() {
            // set starting position of chat window based on window width/height
            this.chatOffsetLeft = (window.innerWidth / 2) - (this.chatStartWidth / 2) - window.innerWidth * 0.05;
            this.chatOffsetTop = - (window.innerHeight / 2) + (this.chatStartHeight / 2) - window.innerHeight * 0.05;

            // set total unread in app messages
            this.totalUnreadInAppMessages = VolieState.currentUser().account_user.total_unread_in_app_messages;

            this.getConversationGroups();

            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "newUserToUserMessage" }}, this.onNewMessage);
            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "userUnreadInAppMessagesCount" }}, this.onUnreadInAppMessagesCountUpdate);
        },
        loadMoreConversationGroups() {
            this.offset += this.limit;
            this.getConversationGroups();
        },
        onNewMessage() {
            this.resetConversationGroups();
        },
        onUnreadInAppMessagesCountUpdate(response: any) {
            this.totalUnreadInAppMessages = response.Data.total_unread_in_app_messages; 
            this.resetConversationGroups();
        },
        readAllConversationGroupUnreadMessages(conversationGroupID, accountID) {
            const options = {
                account_id: accountID,
                conversation_group_id: conversationGroupID,
            }

            ReadAllConversationGroupUnreadMessages(options).then((response) => {
                if (response?.Error && response?.Error !== 'Zero unread messages') {
                    console.error('error reading unread messages:', response?.Error);
                }
            })
            .catch((error) => {
                console.error('error caught when reading unread messages:', error);
            });

            return;
        },
        resetConversationGroups() {
            this.conversationGroups = [];
            this.offset = 0;
            this.getConversationGroups();
        },
        toggleInAppChat() {
            this.displayInAppChat = !this.displayInAppChat;
        },
    },
    components: {
        ConversationGroupCard,
        ConversationView,
        PopupWindow,
    },
    props: {
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        displayGroupName() {
            if (this.isLargeGroupChat) {
                const participants = this.chatParticipants || [];
                return `${participants.slice(0, 2).join(', ')} & ${participants.length - 2} other${participants.length - 2 > 1 ? 's' : ''}`;
            }
            
            return this.tempGroupName;
        },
        isConversationView() {
            return this.activeSlide === 'conversation';
        },
        isGroupsView() {
            return this.activeSlide === 'groups';
        },
        isGroupChat() {
            return this.selectedConversationGroup?.total_participants > 2;
        },
        isLargeGroupChat() {
            return this.selectedConversationGroup?.total_participants > 3;
        },
        selectedConversationGroup() {
            return this.conversationGroups.find(group => group.conversation_group_id === this.selectedConversationGroupId) || null;
        },
        validConversationGroups() {
            return this.conversationGroups?.length > 0;
        },
    },
    created() {
        this.init();
    },
    destroyed() {
        this.removeScrollListener();

        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "newUserToUserMessage" }}, this.onNewMessage);
        this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "userUnreadInAppMessagesCount" }}, this.onUnreadInAppMessagesCountUpdate);
    },
    watch: {
        isShowMoreable(newVal) {
            if (newVal === false) {
                this.removeScrollListener();
            }
        },
        selectedConversationGroup: {
            immediate: true,
            handler(group) {
                if (group) {
                    this.chatParticipants = group.other_user_names?.split(', ') || [];
                    this.tempGroupName = group.other_user_names || '';
                }
            }
        }
    },
})
</script>

<style lang="scss" scoped>
.in-app-chat-container {
    position: relative;
    cursor: pointer;

    .badge {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        right: -5px;
        z-index: 1;
        background-color: var(--error);
        color: white;
        border-radius: 50%;
        font-size: 12px;
        width: 14px;
        height: 14px;
    }
}

.window-body {
  position: relative;
  display: flex;
  flex-direction: column;   
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.main-container {
    position: relative;
    height: 100%;
}

.groups-view, .conversation-view, .new-message-view {
  transition: all 0.2s ease;
  position: absolute;
  width: 100%;
  height: 100%;
}

.slide-x-enter {
    opacity: 0;

    &.groups-view {
        transform: translateX(-100%);
    }
}


.slide-x-leave-to {
    opacity: 0;

    &.groups-view {
        transform: translateX(-100%);
    }

    &.conversation-view {
        transform: translateX(100%);
    }
}

.chat-actions-row {
    border-bottom: 1px solid var(--lt-gray);
    display: flex;
    flex-direction: row;
    align-items: center;
}

.conversation-group-cards {
    overflow-y: auto;
    height: 100%;
}

.see-all-participants {
    cursor: pointer;
    width: 24px;
    display: inline-block;
}

.participants-list {
    padding: 5px;
    list-style-type: none;
    margin: 0;
    background-color: white;
}

.participant-item {
    font-size: 14px;
    padding: 5px;
}
</style>